import dayjs from "dayjs"
import type { ReactNode } from "react"
import {
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react"
import { TrackingTechnologyConsentPage } from "./TrackingTechnologyConsentPage"

import {
  trackingTechnologyConsentDeserializer,
  getTrackingTechnologyConsentExpiration,
  useTrackingTechnologyConsent,
} from "src/hooks"
import { DefaultThemeComponent } from "src/router/DefaultThemeComponent"
import type { TrackingTechnologyConsentResponse } from "src/types"
import {
  setTimeoutForTime,
  getDeviceRegion,
  isUS,
} from "src/utils"

interface Props {
  children: ReactNode;
  responseValue?: string;
  responseUpdatedAt?: string;
  applyDefaultTheme?: boolean;
}

// note we are checking device region not account region
const isUSDevice = isUS(getDeviceRegion())

/*
 * this renders the Tracking Technology Consent prompt unless this device
 * already has a response recorded or is in the US, in which case it renders
 * props.children
 */
export function TrackingTechnologyConsent(props: Props) {
  const {
    applyDefaultTheme,
    responseValue,
    responseUpdatedAt,
  } = props

  const [
    response,
    setResponse,
    removeResponse,
  ] = useTrackingTechnologyConsent()

  // write the response data from props to local storage if defined and valid
  useLayoutEffect(
    () => {
      if (responseValue === undefined || responseUpdatedAt === undefined) {
        return
      }

      // validate the props data
      const propsResponse = trackingTechnologyConsentDeserializer(JSON.stringify({
        value: responseValue,
        updatedAt: responseUpdatedAt,
      }))

      if (propsResponse) {
        setResponse(propsResponse)
      }
    },
    [
      setResponse,
      responseValue,
      responseUpdatedAt,
    ],
  )

  // schedule timeout for clearing response data when it expires
  useEffect(
    () => {
      if (!response) {
        return
      }

      return setTimeoutForTime(
        getTrackingTechnologyConsentExpiration(response.updatedAt),
        () => {
          removeResponse()
        },
      )
    },
    [
      removeResponse,
      response,
    ],
  )

  const setResponseValue = useCallback(
    (newValue: TrackingTechnologyConsentResponse["value"]) => {
      setResponse({
        value: newValue,
        updatedAt: dayjs().toISOString(),
      })
    },
    [setResponse],
  )

  if (isUSDevice || response?.value !== undefined) {
    return (
      <>
        {props.children}
      </>
    )
  }

  if (applyDefaultTheme) {
    return (
      <DefaultThemeComponent>
        <TrackingTechnologyConsentPage setResponse={setResponseValue} />
      </DefaultThemeComponent>
    )
  }

  return (
    <TrackingTechnologyConsentPage setResponse={setResponseValue} />
  )
}
